/**
 * API Client Configuration
 * Base axios instance for all v1 API calls with authentication and error handling
 */

import axios from 'utils/axios';

// API base URL for v1 endpoints
export const API_BASE = '/api/v1';

/**
 * Build URL with query parameters
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @returns {string} Full URL with query string
 */
export const buildUrl = (endpoint, params = {}) => {
  const url = new URL(endpoint, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => url.searchParams.append(key, v));
      } else {
        url.searchParams.append(key, value);
      }
    }
  });

  return url.pathname + url.search;
};

/**
 * Generic GET request
 * @param {string} endpoint - API endpoint (without base)
 * @param {Object} params - Query parameters
 * @returns {Promise} Response data
 */
export const get = async (endpoint, params = {}) => {
  const url = buildUrl(`${API_BASE}${endpoint}`, params);
  const response = await axios.get(url);
  return response.data;
};

/**
 * Generic POST request
 * @param {string} endpoint - API endpoint (without base)
 * @param {Object} data - Request body
 * @param {Object} params - Query parameters
 * @returns {Promise} Response data
 */
export const post = async (endpoint, data = {}, params = {}) => {
  const url = buildUrl(`${API_BASE}${endpoint}`, params);
  const response = await axios.post(url, data);
  return response.data;
};

/**
 * Generic PUT request
 * @param {string} endpoint - API endpoint (without base)
 * @param {Object} data - Request body
 * @param {Object} params - Query parameters
 * @returns {Promise} Response data
 */
export const put = async (endpoint, data = {}, params = {}) => {
  const url = buildUrl(`${API_BASE}${endpoint}`, params);
  const response = await axios.put(url, data);
  return response.data;
};

/**
 * Generic PATCH request
 * @param {string} endpoint - API endpoint (without base)
 * @param {Object} data - Request body
 * @param {Object} params - Query parameters
 * @returns {Promise} Response data
 */
export const patch = async (endpoint, data = {}, params = {}) => {
  const url = buildUrl(`${API_BASE}${endpoint}`, params);
  const response = await axios.patch(url, data);
  return response.data;
};

/**
 * Generic DELETE request
 * @param {string} endpoint - API endpoint (without base)
 * @param {Object} params - Query parameters
 * @returns {Promise} Response data
 */
export const del = async (endpoint, params = {}) => {
  const url = buildUrl(`${API_BASE}${endpoint}`, params);
  const response = await axios.delete(url);
  return response.data;
};

/**
 * Upload file with multipart form data
 * @param {string} endpoint - API endpoint (without base)
 * @param {FormData} formData - Form data with file
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise} Response data
 */
export const upload = async (endpoint, formData, onProgress = null, options = {}) => {
  const url = `${API_BASE}${endpoint}`;
  // `options` is merged last so a caller can override defaults such as `timeout`
  // (the global axios timeout is 30s, too short for uploads the server processes
  // with OCR or a model call before responding).
  const response = await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      : undefined,
    ...options,
  });
  return response.data;
};

/**
 * Download file as blob
 * @param {string} endpoint - API endpoint (without base)
 * @param {Object} params - Query parameters
 * @returns {Promise<Blob>} File blob
 */
export const download = async (endpoint, params = {}) => {
  const url = buildUrl(`${API_BASE}${endpoint}`, params);
  const response = await axios.get(url, {
    responseType: 'blob',
  });
  return response.data;
};

// Export the axios instance for direct use if needed
export default axios;
